/**
 * FeedCard — one family-feed item + its reaction bar (Fase 5).
 *
 * Renders a celebratory moment with the actor's pixel avatar, a kind-specific
 * sentence, an optional point chip (chore / proposal items), a relative
 * timestamp, and the fixed-emoji reaction bar. The message line is composed
 * via t() with interpolation; the achievement title is resolved from the
 * shared ACHIEVEMENTS registry → i18n.
 *
 * a11y (aria-norwegian gate): the reaction buttons carry NO dynamic
 * aria-label — they hold an sr-only <span> with the localized label instead
 * (the ColourSlotPicker pattern), and the visible emoji is aria-hidden.
 */
import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  ACHIEVEMENTS,
  FEED_REACTIONS,
  type FeedReaction,
} from '@stell/shared';
import {
  Card,
  PixelAvatar,
  PointChip,
  RelativeTime,
  type ColorSlot,
} from '../../components/primitives';
import type { FeedItemView } from './useFeedQuery';

const COLOR_SLOTS: ReadonlySet<string> = new Set<ColorSlot>([
  'm-terracotta',
  'm-berry',
  'm-ochre',
  'm-sage',
  'm-sky',
  'm-plum',
]);

function toColorSlot(token: string | null): ColorSlot {
  return token && COLOR_SLOTS.has(token) ? (token as ColorSlot) : 'm-terracotta';
}

/** Leading glyph per kind — sourced from a constant (no JSX string literals). */
const KIND_GLYPH: Record<FeedItemView['kind'], string> = {
  chore_approved: '✅',
  achievement_unlocked: '🏅',
  streak_milestone: '🔥',
  proposal_approved: '💡',
  meal_planned: '🍽️',
};

function achievementTitle(item: FeedItemView, t: TFunction): string {
  const key = typeof item.meta?.key === 'string' ? item.meta.key : item.title;
  const def = (ACHIEVEMENTS as Record<string, { title_key: string } | undefined>)[key];
  return def ? t(def.title_key) : key;
}

/** Setningen for ett innslag. Delt med siste innslag på Hjem (#154). */
export function feedMessage(item: FeedItemView, t: TFunction): string {
  const actor = item.actor_short_name;
  switch (item.kind) {
    case 'chore_approved':
      return t('feed.item.chore_approved', { actor, title: item.title });
    case 'proposal_approved':
      return t('feed.item.proposal_approved', { actor, title: item.title });
    case 'achievement_unlocked':
      return t('feed.item.achievement_unlocked', { actor, achievement: achievementTitle(item, t) });
    case 'streak_milestone': {
      const streak = typeof item.meta?.streak === 'number' ? item.meta.streak : 0;
      return t('feed.item.streak_milestone', { actor, count: streak });
    }
    case 'meal_planned':
      return t('feed.item.meal_planned', { actor, title: item.title });
    default:
      return actor;
  }
}

interface FeedCardProps {
  item: FeedItemView;
  /** Toggle the caller's reaction. */
  onReact: (emoji: FeedReaction) => void;
}

export function FeedCard({ item, onReact }: FeedCardProps): ReactNode {
  const { t } = useTranslation();
  const countByEmoji = new Map<FeedReaction, number>(
    item.reactions.map((r) => [r.emoji, r.count]),
  );

  return (
    <article data-testid="feed-card" data-kind={item.kind}>
      <Card>
        <div className="flex items-start gap-3">
          <PixelAvatar colorSlot={toColorSlot(item.actor_color_token)} size={40} />
          <div className="min-w-0 flex-1">
            <p className="text-ink text-sm leading-snug">
              <span aria-hidden="true">{KIND_GLYPH[item.kind]} </span>
              {feedMessage(item, t)}
            </p>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-ink-2 text-xs">
                <RelativeTime instant={item.created_at} />
              </span>
              {typeof item.points === 'number' && item.points > 0 ? (
                <PointChip points={item.points} variant="gain" />
              ) : null}
            </div>

            {/* Reaction bar — fixed emoji set, one per member (toggle). */}
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="feed-reactions">
              {FEED_REACTIONS.map((emoji) => {
                const count = countByEmoji.get(emoji) ?? 0;
                const mine = item.my_reaction === emoji;
                return (
                  <button
                    key={emoji}
                    type="button"
                    data-testid={`feed-react-${emoji}`}
                    aria-pressed={mine}
                    onClick={() => onReact(emoji)}
                    className={[
                      'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-sm transition-colors',
                      mine
                        ? 'border-primary bg-lime-bg text-ink'
                        : 'border-line text-ink-2 hover:border-primary',
                    ].join(' ')}
                  >
                    <span aria-hidden="true">{emoji}</span>
                    {count > 0 ? <span className="tnum text-xs">{count}</span> : null}
                    <span className="sr-only">{t('feed.reactLabel', { emoji })}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Card>
    </article>
  );
}
